'use client';

/* eslint-disable @next/next/no-img-element */
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { UserDto } from '@vuganawe/types';
import { LanguageSwitcher } from '@/components/shell/LanguageSwitcher';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { NotificationBell } from '@/components/account/NotificationBell';
import { SyncStatusPill } from '@/components/shell/SyncStatusPill';
import { LoadingState } from '@/components/ui/states';
import { useT } from '@/i18n/client';
import { getCurrentUser } from '@/lib/api/public';
import { clearLearnerSession, getLearnerToken, getLearnerUser } from '@/lib/learnerSession';

type ActiveNav =
  | 'home'
  | 'learn'
  | 'review'
  | 'games'
  | 'leaderboard'
  | 'certificates'
  | 'resources';

interface DashboardContextValue {
  user: UserDto | null;
  token: string;
}

const DashboardContext = createContext<DashboardContextValue | null>(null);

/** Read the signed-in learner + token from inside any dashboard screen. */
export function useDashboard(): DashboardContextValue {
  const value = useContext(DashboardContext);
  if (!value) {
    throw new Error('useDashboard must be used within <DashboardShell>');
  }
  return value;
}

/**
 * True while the viewport is in drawer territory. Used to render the mobile
 * bar *or* the desktop topbar tools, never both: NotificationBell polls on a
 * timer and ThemeToggle keeps local state, so a second mounted copy would
 * double-poll and drift out of sync.
 *
 * Safe from hydration mismatches because the shell only renders real chrome
 * after its auth effect has run — the server always emits the loading state.
 */
function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches,
  );

  useEffect(() => {
    const query = window.matchMedia(NARROW_QUERY);
    const sync = () => setNarrow(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  return narrow;
}

/** Must match the drawer breakpoint in account.css. */
const NARROW_QUERY = '(max-width: 900px)';

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('') || 'U'
  );
}

const NAV: { key: ActiveNav; href: string; icon: string; labelKey: string }[] = [
  { key: 'home', href: '/account', icon: '🏠', labelKey: 'dash.navDashboard' },
  { key: 'learn', href: '/account/learn', icon: '📚', labelKey: 'dash.navCourses' },
  { key: 'review', href: '/account/learn/review', icon: '🧠', labelKey: 'dash.navReview' },
  { key: 'games', href: '/account/learn/games', icon: '🎮', labelKey: 'dash.navGames' },
  { key: 'leaderboard', href: '/account/leaderboard', icon: '🏆', labelKey: 'dash.navLeaderboard' },
  { key: 'certificates', href: '/account/certificates', icon: '🎖️', labelKey: 'dash.navCertificates' },
  { key: 'resources', href: '/resources', icon: '📂', labelKey: 'dash.navResources' },
];

interface DashboardShellProps {
  active: ActiveNav;
  title: string;
  subtitle?: string;
  /** When true, the topbar shows "Hello, {first name}" instead of {title}. */
  greetUser?: boolean;
  /** Optional content rendered on the right side of the topbar (e.g. a back link). */
  topbarAside?: ReactNode;
  children: ReactNode;
}

/**
 * App-like chrome shared by every signed-in screen: auth guard, sidebar nav,
 * and the greeting topbar. Children render inside the main content column and
 * can read the learner via {@link useDashboard}.
 */
export function DashboardShell({
  active,
  title,
  subtitle,
  greetUser,
  topbarAside,
  children,
}: DashboardShellProps) {
  const t = useT();
  const router = useRouter();
  const pathname = usePathname();
  const [user, setUser] = useState<UserDto | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  /** Phone/tablet only: the sidebar is an off-canvas drawer below 900px. */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isNarrow = useIsNarrow();

  useEffect(() => {
    const learnerToken = getLearnerToken();
    if (!learnerToken) {
      const next = typeof window !== 'undefined' ? window.location.pathname : '/account';
      router.replace(`/account/login?next=${encodeURIComponent(next)}`);
      return;
    }
    setToken(learnerToken);
    setUser(getLearnerUser());
    setReady(true);

    void getCurrentUser(learnerToken)
      .then(setUser)
      .catch(() => {
        clearLearnerSession();
        router.replace('/account/login?next=/account');
      });
  }, [router]);

  // Navigating inside the drawer must close it — the destination renders
  // underneath it otherwise.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Freeze the page behind the drawer so swiping the nav doesn't scroll the
  // screen under it, and let Escape close it.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setDrawerOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [drawerOpen]);

  function signOut() {
    clearLearnerSession();
    router.replace('/');
    router.refresh();
  }

  if (!ready || !token) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <LoadingState label={t('dash.loadingDashboard')} />
      </div>
    );
  }

  return (
    <DashboardContext.Provider value={{ user, token }}>
      <div
        className={`vn-dash${sidebarCollapsed ? ' is-sidebar-collapsed' : ''}${
          drawerOpen ? ' is-drawer-open' : ''
        }`}
      >
        {/* Phone/tablet chrome, replacing the permanent sidebar below 901px. */}
        {isNarrow ? (
          <header className="vn-dash__mobilebar">
            <button
              type="button"
              className="vn-dash__burger"
              aria-label={t('dash.openMenu')}
              aria-expanded={drawerOpen}
              aria-controls="dash-nav"
              onClick={() => setDrawerOpen(true)}
            >
              <span aria-hidden="true">☰</span>
            </button>
            <Link href="/account" className="vn-dash__mobilebrand">
              <img src="/logo.png" alt="" width={28} height={28} />
              <span>Vuganawe</span>
            </Link>
            <div className="vn-dash__mobilebar-actions">
              <SyncStatusPill />
              <NotificationBell />
              <span className="vn-dash__avatar vn-dash__avatar--sm" aria-hidden="true">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="vn-dash__avatar-img" />
                ) : user ? (
                  initials(user.name)
                ) : (
                  'U'
                )}
              </span>
            </div>
          </header>
        ) : null}

        <button
          type="button"
          className="vn-dash__scrim"
          aria-label={t('dash.closeMenu')}
          tabIndex={drawerOpen ? 0 : -1}
          onClick={() => setDrawerOpen(false)}
        />

        <aside className="vn-dash__sidebar" id="dash-nav">
          <div className="vn-dash__sidehead">
            <Link href="/" className="vn-dash__brand" title="Vuganawe">
              <img src="/logo.png" alt="" width={32} height={32} />
              <span className="vn-dash__brand-text">Vuganawe</span>
            </Link>
            <button
              type="button"
              className="vn-dash__collapse"
              aria-label={sidebarCollapsed ? t('dash.expandSidebar') : t('dash.collapseSidebar')}
              aria-pressed={sidebarCollapsed}
              onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
              title={sidebarCollapsed ? t('dash.expandSidebar') : t('dash.collapseSidebar')}
            >
              {sidebarCollapsed ? '›' : '‹'}
            </button>
            <button
              type="button"
              className="vn-dash__drawerclose"
              aria-label={t('dash.closeMenu')}
              onClick={() => setDrawerOpen(false)}
            >
              <span aria-hidden="true">✕</span>
            </button>
          </div>
          <ul className="vn-dash__nav">
            {NAV.map((item) => (
              <li key={item.key}>
                <Link
                  href={item.href}
                  className={`vn-dash__navlink${active === item.key ? ' is-active' : ''}`}
                >
                  <span className="vn-dash__navicon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="vn-dash__navtext">{t(item.labelKey)}</span>
                </Link>
              </li>
            ))}
            <li>
              <Link href="/" className="vn-dash__navlink">
                <span className="vn-dash__navicon" aria-hidden="true">
                  ↩
                </span>
                <span className="vn-dash__navtext">{t('dash.navBackToSite')}</span>
              </Link>
            </li>
          </ul>
          {/* Theme + language live in the topbar on desktop; on phones they
              move down here so the topbar stays a title and nothing else. */}
          {isNarrow ? (
            <div className="vn-dash__drawerutils">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
          ) : null}
          <button
            type="button"
            className="vn-btn vn-btn--secondary vn-dash__signout"
            onClick={signOut}
            title={t('common.signOut')}
          >
            <span className="vn-dash__signout-icon" aria-hidden="true">
              ↩
            </span>
            <span className="vn-dash__navtext">{t('common.signOut')}</span>
          </button>
        </aside>

        <div className="vn-dash__main">
          <div className="vn-dash__topbar">
            <div className="vn-dash__greeting">
              <h1>
                {greetUser
                  ? `${t('dash.hello')}${user ? `, ${user.name.split(' ')[0]}` : ''}`
                  : title}
              </h1>
              {subtitle ? <p>{subtitle}</p> : null}
            </div>
            <div className="vn-dash__topbar-aside">
              {/* Below 901px these live in the mobile bar and drawer instead. */}
              {isNarrow ? null : (
                <span className="vn-dash__topbar-tools">
                  <SyncStatusPill />
                  <NotificationBell />
                  <ThemeToggle />
                  <LanguageSwitcher />
                </span>
              )}
              {/* Page-specific, not a duplicated utility — keep it everywhere. */}
              {topbarAside}
              {isNarrow ? null : (
                <div className="vn-dash__avatar" aria-hidden="true">
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt="" className="vn-dash__avatar-img" />
                  ) : user ? (
                    initials(user.name)
                  ) : (
                    'U'
                  )}
                </div>
              )}
            </div>
          </div>

          {children}
        </div>
      </div>
    </DashboardContext.Provider>
  );
}
